import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import 'react-toastify/dist/ReactToastify.css';
import "./index.css";
import App from "./App.tsx";
import "./firebase.js";
import { applyStoredAppFont, scheduleAppFontDefaultRefresh } from "./lib/appFont";
import { installChunkErrorReloadHandler } from "./lib/lazyWithRetry";
import { captureCloudMarketplaceActivationRedirect } from "./utils/cloudMarketplaceActivationState";

// Build-native fallback for stale-deploy chunk load failures (Safari included).
installChunkErrorReloadHandler();
applyStoredAppFont();
scheduleAppFontDefaultRefresh();

// Marketplace activation state is a bearer credential. Capture and remove it
// before telemetry is initialized.
captureCloudMarketplaceActivationRedirect();

// VPC testing: monkey-patch fetch to replace auth with API key from ?api_key=
// URL param (persisted to localStorage so SPA navigation keeps the key).
// Patches requests to app origin, relative paths, and VITE_BACKEND_DOMAIN (cross-port dev setup).
import { persistVpcApiKeyFromUrl, getVpcApiKey, isVpcMode } from "./utils/vpcMode";
(() => {
  persistVpcApiKeyFromUrl();
  const apiKey = getVpcApiKey();
  if (!apiKey) return;
  const appOrigin = window.location.origin;
  const backendOrigin = import.meta.env.VITE_BACKEND_DOMAIN?.replace(/\/$/, '') || '';
  const origFetch = window.fetch;
  window.fetch = function(input: RequestInfo | URL, init?: RequestInit) {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
    const isAppRequest = url.startsWith(appOrigin) || url.startsWith('/') || (backendOrigin && url.startsWith(backendOrigin));
    if (!isAppRequest) return origFetch.call(this, input, init);
    const headers = new Headers(init?.headers || {});
    headers.delete('Authorization');
    headers.set('X-API-Key', apiKey);
    return origFetch.call(this, input, { ...init, headers });
  };
})();
import { AuthProvider } from "./contexts/AuthContext";
import { ToastContainer } from 'react-toastify';
import 'video.js/dist/video-js.css';
import { CreditProvider } from "./contexts/CreditContext.tsx";
import { DatasetExportJobsProvider } from "./contexts/DatasetExportJobsContext.tsx";
import { Log2simJobsProvider } from "./contexts/Log2simJobsContext.tsx";
import { WhiteLabelProvider } from "./contexts/WhiteLabelContext.tsx";

import * as Sentry from "@sentry/react";
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react'
import { IS_PROD, API_BASE_URL } from "./config.ts";
import { injectSpeedInsights } from '@vercel/speed-insights';
import { Analytics } from '@vercel/analytics/react';

// Telemetry is SaaS-only. A customer VPC has no egress, so none of these SDKs
// could deliver anyway, and a self-hosted deployment must not ship its users'
// data to third parties -- Sentry here runs with sendDefaultPii: true.
if (!isVpcMode()) {
  Sentry.init({
    dsn: "https://04e37f692c193834570daa8d8f6196e1@o4509747949469696.ingest.us.sentry.io/4509747951828992",
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    // Set environment based on hostname
    environment: IS_PROD ? "production" : "development",
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Logs
    enableLogs: true,
    // Add release to track deployments
    release: `frontend@${import.meta.env.VITE_APP_VERSION || "1.0.0"}`
  });

  posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    opt_out_capturing_by_default: false,
    loaded: function (ph) {
        if (!IS_PROD || API_BASE_URL !== 'https://api-prod.nomadicml.com/api') {
            ph.opt_out_capturing(); // opts a user out of event capture
            ph.set_config({ disable_session_recording: true });
        }
    }, 
  });

  // Initialize Vercel Speed Insights
  injectSpeedInsights();
}


// Download the route while AuthProvider restores the session. This imports
// code only; authorization and data reads still begin when the route mounts.
// On a cold visit the Workbench chunk otherwise waits for /users/me first.
if (/^\/(?:live-demo\/)?workbench\//.test(window.location.pathname)) {
  void import("./components/BatchWorkbench").catch(() => {
    // The route's lazyWithRetry loader owns visible errors and recovery.
  });
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AuthProvider>
      <DatasetExportJobsProvider>
        <Log2simJobsProvider>
          <CreditProvider>
            <WhiteLabelProvider>
              <PostHogProvider client={posthog}>
                <App />
              </PostHogProvider>
              <ToastContainer />
            </WhiteLabelProvider>
          </CreditProvider>
        </Log2simJobsProvider>
      </DatasetExportJobsProvider>
    </AuthProvider>
    {!isVpcMode() && <Analytics />}
  </StrictMode>
);
